import { Hook } from '../hooks/Hook'
import { AudioState } from './recorder'
import * as PubSub from './pubSub'
import { UploadAttrs } from './webRecorder2'

// TODO: Use this when we change meetings/topics
export function setSubjectId(hook: Hook, subjectId: string): void {
  hook.pushEventTo(hook.el, 'set_subject_id', { subjectId })
}

// TODO: Subscribe to all meetings statuses
export function subscribeToStatus(hook: Hook, subjectId: string): void {
  hook.pushEventTo(hook.el, 'subscribe_to_status', { subjectId })
}

export function trackAnalytics(hook: Hook, message: string, extra?: Record<string, any>): void {
  hook.pushEventTo(hook.el, 'track_analytics', { message, extra })
}

export function setCriticalBanner(hook: Hook, message: string): void {
  hook.pushEventTo(hook.el, 'set_critical_banner', { message })
}

export function clearCriticalBanner(hook: Hook): void {
  hook.pushEventTo(hook.el, 'clear_critical_banner', {})
}

export async function broadcastRecordingState(hook: Hook, state: AudioState): Promise<void> {
  return new Promise((resolve) => {
    hook.pushEventTo(hook.el, 'update_recording_state', { state }, (_reply) => {
      resolve()
    })
  })
}

const maxUploadProgressSeconds = 30

export function uploadRecording(hook: Hook, attrs: UploadAttrs): Promise<void> {
  return new Promise((resolve, reject) => {
    const push = (event: string, data: any, then?: (reply: any) => void) => hook.pushEventTo(hook.el, event, data, then)

    const { recordingStartedAtUnixMs, mimeType, subjectId, audioChunks } = attrs

    push('upload', { recordingStartedAtUnixMs, subjectId, mimeType }, (reply) => {
      if (reply && 'errorMessage' in reply) {
        reject(new Error(reply.errorMessage))
        return
      }

      // TODO: Store the cloud file ID, associate it with the meeting, and subscribe to future updates on it
      const { url, name, cloud_file_id } = reply as { url: string; name: string; cloud_file_id: string }

      const blob = new Blob(audioChunks, { type: mimeType })
      const file = new File([blob], name, { type: mimeType })

      let xhr = new XMLHttpRequest()

      // If we go 30 seconds between progress events, we should assume the connection is dead.
      let progressTimeout = makeProgressTimeout(xhr)

      xhr.open('PUT', url, true)
      xhr.setRequestHeader('Cache-Control', '2592000')
      xhr.setRequestHeader('Content-Type', mimeType)

      xhr.onload = () => {
        clearTimeout(progressTimeout)

        /* eslint-disable-next-line no-console */
        console.log('xhr.status', xhr.status)

        if (xhr.status === 200) {
          resolve()
        } else {
          let error = `${xhr.statusText} ${xhr.responseText}`
          /* eslint-disable-next-line no-console */
          console.error('Error:', xhr.status, error)

          push('upload_error', {
            status: xhr.status,
            status_text: error,
            cloud_file_id: cloud_file_id,
          })

          reject(new Error(error))
        }
      }

      xhr.upload.addEventListener('progress', (event) => {
        /* eslint-disable-next-line no-console */
        console.log('Upload progress', event.loaded, '/', event.total, 'bytes')

        clearTimeout(progressTimeout)
        progressTimeout = makeProgressTimeout(xhr)

        const progressPercent = Math.round((event.loaded / event.total) * 100)
        PubSub.publish('apply-action', { action: 'update-upload-progress', progressPercent, subjectId })
      })

      xhr.onerror = () => {
        clearTimeout(progressTimeout)
        /* eslint-disable-next-line no-console */
        console.error('Network error or failure in sending the request.')
        push('log', { message: 'Network error or failure in sending the request.' })
        reject(new Error('Network error while uploading capture.'))
      }

      xhr.onabort = () => {
        // The abort callback *only* fires when *we* aborted due to a  timeout.
        /* eslint-disable-next-line no-console */
        console.warn(`Upload timed out after ${maxUploadProgressSeconds} seconds.`)
        push('log', { message: 'Upload timed out' })
        reject(new Error('Upload timed out.'))

        clearTimeout(progressTimeout)
      }

      xhr.send(file)
    })
  })
}

function makeProgressTimeout(xhr: XMLHttpRequest) {
  return setTimeout(() => {
    xhr.abort()
  }, maxUploadProgressSeconds * 1_000)
}
